import { useState } from "react";
import { Sparkles, RefreshCw, Users, ShoppingBag, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { generateFakeUsers, generateFakeOrders, deleteFakeData } from "../api/apiServices";

/* ─────────────────────── count selector ─────────────────────── */
function CountSelector({ value, onChange, min = 1, max = 50 }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 font-bold transition-all"
      >
        −
      </button>
      <span className="w-10 text-center text-sm font-bold text-gray-700">{value}</span>
      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 font-bold transition-all"
      >
        +
      </button>
    </div>
  );
}

/* ─────────────────────── log item ─────────────────────── */
function LogItem({ entry }) {
  const isSuccess = entry.type === "success";
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${
        isSuccess
          ? "bg-green-50 border-green-100 text-green-800"
          : "bg-red-50 border-red-100 text-red-700"
      }`}
    >
      {isSuccess
        ? <CheckCircle2 size={15} className="text-green-500 shrink-0 mt-0.5" />
        : <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />
      }
      <div className="flex-1 min-w-0">
        <p className="font-medium">{entry.msg}</p>
        <p className="text-xs opacity-60 mt-0.5">{entry.time}</p>
      </div>
    </motion.div>
  );
}

/* ─────────────────────── main component ─────────────────────── */
/**
 * GenerateDemoData
 *
 * A self-contained admin tool for seeding the platform with demo data.
 * Mount this only inside a protected admin route — e.g.:
 *
 *   <Route
 *     path="/admin/generate-demo"
 *     element={<RequireAdmin><GenerateDemoData /></RequireAdmin>}
 *   />
 *
 * Props:
 *   onGenerated?: () => void   — optional callback after any successful generation
 *                                (useful if you want to refresh a parent dashboard)
 */
export default function GenerateDemoData({ onGenerated }) {
  const [userCount,  setUserCount]  = useState(10);
  const [orderCount, setOrderCount] = useState(5);
  const [userRole,   setUserRole]   = useState("");   
  const [log,        setLog]        = useState([]);
  const [busyUsers,  setBusyUsers]  = useState(false);
  const [busyOrders, setBusyOrders] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);

  const addLog = (msg, type = "success") => {
    setLog((prev) => [
      { msg, type, time: new Date().toLocaleTimeString() },
      ...prev,
    ].slice(0, 30));
  };

  const handleGenerateUsers = async () => {
    setBusyUsers(true);
    try {
      const res = await generateFakeUsers(userCount, userRole || null);
      const msg = `✓ Added ${res.inserted} user${res.inserted !== 1 ? "s" : ""}${userRole ? ` (${userRole})` : ""}`;
      toast.success(msg);
      addLog(msg, "success");
      onGenerated?.();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to generate users";
      toast.error(msg);
      addLog(msg, "error");
    } finally {
      setBusyUsers(false);
    }
  };

  const handleGenerateOrders = async () => {
    setBusyOrders(true);
    try {
      const res = await generateFakeOrders(orderCount);
      const msg = `✓ Added ${res.inserted} order${res.inserted !== 1 ? "s" : ""}`;
      toast.success(msg);
      addLog(msg, "success");
      onGenerated?.();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to generate orders";
      toast.error(msg);
      addLog(msg, "error");
    } finally {
      setBusyOrders(false);
    }
  };

  // ==================== DELETE FAKE DATA ====================
  const handleDeleteFakeData = async () => {
    if (!window.confirm("⚠️ This will delete ALL fake users and orders.\n\nAre you sure you want to continue?")) {
      return;
    }

    setBusyDelete(true);
    try {
      const res = await deleteFakeData();

      const msg = `🧹 Cleaned up ${res.deletedUsers} fake users and ${res.deletedOrders} fake orders`;
      toast.success(msg);
      addLog(msg, "success");
      onGenerated?.();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete fake data";
      toast.error(msg);
      addLog(msg, "error");
    } finally {
      setBusyDelete(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
            <Sparkles size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Demo Data Generator</h1>
            <p className="text-sm text-gray-400">Admin only — seed the platform with sample data</p>
          </div>
        </div>

        {/* warning banner */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
          <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 leading-relaxed">
            Data generated here is stored in the database and will appear on the public dashboard. 
            Use the cleanup endpoint to remove all generated data when needed.
          </p>
        </div>

        {/* generate users card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users size={17} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-700">Generate Users</h2>
              <p className="text-xs text-gray-400">Creates users with Nigerian names and contact details</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* count */}
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Count (max 50)</label>
              <CountSelector value={userCount} onChange={setUserCount} max={50} />
            </div>

            {/* role */}
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Role</label>
              <div className="flex gap-2">
                {["", "user", "vendor", "rider"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setUserRole(r)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
                      userRole === r
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-200 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {r || "Random"}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerateUsers}
              disabled={busyUsers}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all"
            >
              {busyUsers
                ? <><RefreshCw size={14} className="animate-spin" /> Generating…</>
                : <><Sparkles size={14} /> Generate {userCount} User{userCount !== 1 ? "s" : ""}</>
              }
            </button>
          </div>
        </div>

        {/* generate orders card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
              <ShoppingBag size={17} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-700">Generate Orders</h2>
              <p className="text-xs text-gray-400">Creates orders across random stores with items and delivery info</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Count (max 50)</label>
              <CountSelector value={orderCount} onChange={setOrderCount} max={50} />
            </div>

            <button
              onClick={handleGenerateOrders}
              disabled={busyOrders}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 transition-all"
            >
              {busyOrders
                ? <><RefreshCw size={14} className="animate-spin" /> Generating…</>
                : <><Sparkles size={14} /> Generate {orderCount} Order{orderCount !== 1 ? "s" : ""}</>
              }
            </button>
          </div>
        </div>

        {/* ====================== CLEANUP CARD ====================== */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
              <Trash2 size={17} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-700">Clean Fake Data</h2>
              <p className="text-xs text-gray-400">Remove all previously generated fake users and orders</p>
            </div>
          </div>

          <button
            onClick={handleDeleteFakeData}
            disabled={busyDelete}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-all"
          >
            {busyDelete ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Deleting Fake Data...
              </>
            ) : (
              <>
                <Trash2 size={14} />
                Delete All Fake Data
              </>
            )}
          </button>

          <p className="text-[10px] text-center text-red-500 mt-3">
            This action cannot be undone
          </p>
        </div>

        {/* generation log */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-600">Generation Log</h2>
            {log.length > 0 && (
              <button
                onClick={() => setLog([])}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {log.length === 0 ? (
            <p className="text-sm text-gray-300 text-center py-6">No activity yet</p>
          ) : (
            <AnimatePresence>
              <div className="space-y-2">
                {log.map((entry, i) => <LogItem key={i} entry={entry} />)}
              </div>
            </AnimatePresence>
          )}
        </div>

      </div>
    </div>
  );
}
